/** @format */

import { date_range_config, getRangeDateValue } from 'src/components/QuickRangeDate/constants'
import { ServiceOverviewSearchProps } from './typescript'

// 筛选默认值
export const DEFAULT_AGGREGATE_TYPE_SEC = 600

/** HeaderFilter 默认快捷时间范围 */
export const DEFAULT_HEADER_FILTER_RANGE = date_range_config['近3天']

/**
 * QuickRangeDate 快捷项内部 key 列表（即 date_range_config 的 values）
 */
export const HEADER_FILTER_QUICK_RANGE_KEYS: string[] = Object.values(date_range_config) as string[]

/** 「24小时聚合」对应秒数，与下拉项 value 一致 */
export const AGGREGATE_TYPE_24H_SEC = 86400

// 聚合方式可选值
export const AggregateTypeOptions = [
  {
    label: '30秒聚合',
    value: 30
  },
  {
    label: '1分钟聚合',
    value: 60
  },
  {
    label: '5分钟聚合',
    value: 300
  },
  {
    label: '10分钟聚合',
    value: 600
  },
  {
    label: '30分钟聚合',
    value: 1800
  },
  {
    label: '1小时聚合',
    value: 3600
  },
  {
    label: '6小时聚合',
    value: 21600
  },
  {
    label: '12小时聚合',
    value: 43200
  },
  {
    label: '24小时聚合',
    value: AGGREGATE_TYPE_24H_SEC
  }
]

/** 聚合下拉单项类型（与 AggregateTypeOptions 一致） */
export type AggregateTypeOption = typeof AggregateTypeOptions[number]

const MS_PER_MINUTE = 60 * 1000
const MS_PER_HOUR = 60 * MS_PER_MINUTE
const MS_PER_DAY = 24 * MS_PER_HOUR

/**
 * 根据时间跨度返回允许的聚合周期 value（秒）；规律见下方注释。
 */
export function getAllowedAggregateValuesByDurationMs(durationMs: number): Set<number> {
  const d = Math.abs(durationMs)
  // 30分钟
  if (d <= MS_PER_MINUTE * 30) {
    return new Set([60])
  }
  // 1小时
  if (d <= MS_PER_HOUR) {
    return new Set([60, 300, 600])
  }
  // 1天
  if (d <= MS_PER_DAY) {
    return new Set([600, 1800, 3600])
  }
  // 3天
  if (d <= 3 * MS_PER_DAY) {
    return new Set([1800, 3600])
  }
  // 15天
  if (d <= 15 * MS_PER_DAY) {
    return new Set([3600, 21600])
  }
  return new Set([21600, 43200])
}

/**
 * 根据时间范围计算可选聚合周期（顺序与 AggregateTypeOptions 一致）。
 * @param rangeStartMs 范围起点时间戳（毫秒）
 * @param rangeEndMs 范围终点时间戳（毫秒）
 */
export function getAggregateTypeOptionsByTimeRange(
  rangeStartMs: number,
  rangeEndMs: number
): AggregateTypeOption[] {
  const allowed = getAllowedAggregateValuesByDurationMs(rangeEndMs - rangeStartMs)
  return AggregateTypeOptions.filter(opt => allowed.has(opt.value)) || []
}

/** 将 aggregateType 校正为当前时间范围下允许的档位（历史缓存中曾出现过 0 时也会改写） */
export function coerceAggregateTypeForRange(
  rangeStartMs: number,
  rangeEndMs: number,
  aggregateType: number
): number {
  const opts = getAggregateTypeOptionsByTimeRange(rangeStartMs, rangeEndMs)
  if (!opts.length) return DEFAULT_AGGREGATE_TYPE_SEC
  if (aggregateType > 0 && opts.some(o => o.value === aggregateType)) return aggregateType
  return opts[0].value
}

// 默认时间搜索条件
export const initSearch = (): ServiceOverviewSearchProps => {
  const time = getRangeDateValue(DEFAULT_HEADER_FILTER_RANGE, true) as [number, number]
  const [startMs, endMs] = [time[0] * 1000, time[1] * 1000]
  return {
    time,
    aggregateType: coerceAggregateTypeForRange(startMs, endMs, DEFAULT_AGGREGATE_TYPE_SEC)
  }
}
